"use client";

import { useSession } from "@/components/SupabaseProvider";
import { ArrowUpIcon, CheckCircleIcon } from "@heroicons/react/20/solid";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import posthog from "posthog-js";
import {
  FC,
  FormEvent,
  KeyboardEvent,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import Button from "./Button";
import Spinner from "./Spinner";
import Typography from "./Typography";

// AI cancel flow: the test arm of the "cancel-flow-ai-chat" experiment. The
// user talks to Klap's assistant (Claude, server-side) instead of filling the
// Churnkey form. "Cancel subscription" stays one click away the whole time.
// Offers are plain conversation: the assistant says it will see what it can
// do, the chat shows it checking, then it proposes the offer in text. If the
// user says yes, the assistant applies it (server-side) and confirms, and the
// chat ends with a Close button.

type AppliedOffer = {
  offer: { id: string; kind: "pause" | "discount"; title: string };
  /** Pauses: when billing resumes (YYYY-MM-DD). */
  resumes_on: string | null;
};

type ServerItem =
  | { type: "text"; text: string }
  /** Ends a reply in which an offer was prepared: fetch the continuation. */
  | { type: "checking" }
  /** The offer the user agreed to is on their subscription: the chat is over. */
  | ({ type: "applied" } & AppliedOffer);

type Item =
  | { type: "assistant"; text: string }
  | { type: "user"; text: string }
  | ({ type: "applied" } & AppliedOffer);

type Phase =
  | { name: "starting" }
  | { name: "chatting" }
  | { name: "confirm_cancel" }
  | { name: "saved" }
  | { name: "canceled" };

type RetentionChatProps = {
  subscriptionId: string;
  /** Called when the chat can't start: the caller falls back to Churnkey. */
  onUnavailable: (reason: string) => void;
  onClose: () => void;
};

const API = `${process.env.NEXT_PUBLIC_API_URL}/billing/retention-chat`;

/** "Checking" stays up at least this long, even when the reply is faster. */
const MIN_CHECKING_MS = 2500;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// Assistant messages are signed "Klap" with the logomark, messenger-style:
// avatar and name on the first message of each run.
const AssistantRow = ({ first, children }: { first: boolean; children: ReactNode }) => (
  <div className="flex gap-2 self-start max-w-[85%]">
    {first ? (
      <img src="/svg/logomark.svg" alt="" className="w-7 h-7 rounded-lg shrink-0 mt-5" />
    ) : (
      <div className="w-7 shrink-0" />
    )}
    <div className="flex flex-col gap-1 min-w-0">
      {first && (
        <Typography variant="caption" className="text-foreground-muted">
          Klap
        </Typography>
      )}
      {children}
    </div>
  </div>
);

const TypingDots = () => (
  <div className="flex gap-1 px-4 py-3 rounded-2xl rounded-tl-md bg-surface-secondary w-fit">
    {[0, 150, 300].map((delay) => (
      <span
        key={delay}
        className="w-1.5 h-1.5 rounded-full bg-foreground-muted animate-bounce"
        style={{ animationDelay: `${delay}ms` }}
      />
    ))}
  </div>
);

const CheckingIndicator = () => (
  <div className="flex items-center gap-2 px-4 py-2.5 rounded-2xl rounded-tl-md bg-surface-secondary w-fit">
    <Spinner className="w-4 h-4 text-foreground-muted" />
    <Typography variant="body2" className="text-foreground-muted">
      Checking what we can offer you…
    </Typography>
  </div>
);

const toItems = (serverItems: ServerItem[]): Item[] =>
  serverItems.flatMap((item): Item[] =>
    item.type === "text"
      ? [{ type: "assistant", text: item.text }]
      : item.type === "applied"
        ? [item]
        : []
  );

const AppliedNote = ({ offer, resumes_on }: AppliedOffer) => (
  <div className="self-center flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-50 text-green-700">
    <CheckCircleIcon className="w-4 h-4" />
    <Typography variant="caption">
      {offer.kind === "pause" && resumes_on
        ? `Subscription paused until ${dayjs(resumes_on).format("MMMM D, YYYY")}`
        : `Applied to your subscription: ${offer.title.charAt(0).toLowerCase()}${offer.title.slice(1)}`}
    </Typography>
  </div>
);

const RetentionChat: FC<RetentionChatProps> = ({ subscriptionId, onUnavailable, onClose }) => {
  const session = useSession();
  const queryClient = useQueryClient();
  const [items, setItems] = useState<Item[]>([]);
  const [phase, setPhase] = useState<Phase>({ name: "starting" });
  const [draft, setDraft] = useState("");
  const [waiting, setWaiting] = useState(false);
  const [checking, setChecking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [periodEnd, setPeriodEnd] = useState<string | null>(null);
  // The real support chat (Intercom), when it's loaded for this user.
  const [canReachTeam] = useState(
    () => typeof window !== "undefined" && typeof window.Intercom === "function"
  );
  const chatIdRef = useRef<string | null>(null);
  const startedRef = useRef(false);
  const finalizedRef = useRef(false);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const call = useCallback(
    async (path: string, body: object = {}, keepalive = false) => {
      const res = await fetch(`${API}${path}`, {
        method: "POST",
        keepalive,
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
          "content-type": "application/json",
        },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const err = new Error(data?.message || "Request failed") as Error & { code?: string };
        err.code = data?.error_code;
        throw err;
      }
      return data;
    },
    [session?.access_token]
  );

  // Insights + completion events, whatever the outcome. keepalive lets it
  // finish when the tab is closing; the server sweeps whatever never arrives.
  const finalize = useCallback(() => {
    const chatId = chatIdRef.current;
    if (!chatId || finalizedRef.current) return;
    finalizedRef.current = true;
    call(`/${chatId}/finalize`, {}, true).catch(() => {});
  }, [call]);

  // Finalize when the chat unmounts (modal closed) or the tab goes away, and
  // only then: `finalize` changes identity when the session token refreshes,
  // which must not end a chat in progress.
  const finalizeRef = useRef(finalize);
  useEffect(() => {
    finalizeRef.current = finalize;
  }, [finalize]);
  useEffect(() => {
    const onPageHide = () => finalizeRef.current();
    window.addEventListener("pagehide", onPageHide);
    return () => {
      window.removeEventListener("pagehide", onPageHide);
      finalizeRef.current();
    };
  }, []);

  // Shows a reply. One ending in "checking" means the assistant prepared an
  // offer: show that it's checking, then fetch the continuation where it
  // proposes it (if that fails, it proposes it with its next reply). One with
  // "applied" means the user's yes was applied: the chat is over.
  const showReply = useCallback(
    async (chatId: string, serverItems: ServerItem[]) => {
      setItems((prev) => [...prev, ...toItems(serverItems)]);
      if (serverItems.some((item) => item.type === "applied")) {
        setPhase({ name: "saved" });
        finalizeRef.current();
        queryClient.invalidateQueries({ queryKey: ["subscriptions"] });
        return;
      }
      if (serverItems.at(-1)?.type !== "checking") return;
      setChecking(true);
      try {
        const [data] = await Promise.all([
          call(`/${chatId}/messages`, { continue: true }),
          sleep(MIN_CHECKING_MS),
        ]);
        await showReply(chatId, data.messages);
      } catch {
        setError("Something went wrong. Send a message to continue, or cancel with the link below.");
      } finally {
        setChecking(false);
      }
    },
    [call, queryClient]
  );

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    setWaiting(true);
    call("", {
      subscription_id: subscriptionId,
      browser_language: typeof navigator !== "undefined" ? navigator.language : null,
      support_link_shown: canReachTeam,
    })
      .then((data) => {
        chatIdRef.current = data.chat_id;
        setPeriodEnd(data.current_period_end);
        setPhase({ name: "chatting" });
        setWaiting(false);
        return showReply(data.chat_id, data.messages);
      })
      .catch((err) => {
        setWaiting(false);
        posthog.capture("Retention Chat Start Failed", {
          subscriptionId,
          error_code: err?.code ?? null,
        });
        onUnavailable(err?.code ?? "error");
      });
  }, [call, showReply, subscriptionId, onUnavailable, canReachTeam]);


  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
  }, [items, waiting, checking, phase.name]);

  const thinking = waiting || checking;

  useEffect(() => {
    if (phase.name === "chatting" && !thinking) inputRef.current?.focus();
  }, [phase.name, thinking]);

  const send = async (payload: { content: string }) => {
    const chatId = chatIdRef.current;
    if (!chatId || thinking) return;
    setError(null);
    setWaiting(true);
    let data;
    try {
      data = await call(`/${chatId}/messages`, payload);
    } catch (err: any) {
      setError(
        err?.code === "too_many_messages"
          ? "That's all the questions we have. You can keep your subscription or cancel below."
          : "Something went wrong. Try again, or cancel with the link below."
      );
      // Give the unsent text back.
      setItems((prev) => prev.slice(0, -1));
      setDraft(payload.content);
      return;
    } finally {
      setWaiting(false);
    }
    await showReply(chatId, data.messages);
  };

  const handleSubmit = (e?: FormEvent) => {
    e?.preventDefault();
    const text = draft.trim();
    if (!text || thinking) return;
    setItems((prev) => [...prev, { type: "user", text }]);
    setDraft("");
    send({ content: text });
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      handleSubmit();
    }
  };

  // "Need technical support? Talk to the team": opens Intercom over the chat, which stays
  // open behind it.
  const messageTeam = () => {
    posthog.capture("Retention Chat Support Contacted", { chat_id: chatIdRef.current });
    window.Intercom?.(
      "showNewMessage",
      "Hi, I need help with a technical issue on Klap."
    );
  };

  const confirmCancel = async () => {
    const chatId = chatIdRef.current;
    if (!chatId || busy) return;
    setBusy(true);
    setError(null);
    try {
      await call(`/${chatId}/cancel`);
      setPhase({ name: "canceled" });
      finalize();
      queryClient.invalidateQueries({ queryKey: ["subscriptions"] });
    } catch {
      setError("We couldn't cancel your subscription. Please try again or contact support.");
    } finally {
      setBusy(false);
    }
  };

  const periodEndLabel = periodEnd ? dayjs(periodEnd).format("MMMM D, YYYY") : "the end of your billing period";

  if (phase.name === "canceled") {
    return (
      <div className="flex flex-col items-center text-center gap-3 py-4">
        <CheckCircleIcon className="w-10 h-10 text-green-600" />
        <Typography variant="h5">Your subscription is canceled</Typography>
        <Typography variant="body2" className="text-foreground-muted max-w-sm">
          You keep full access until {periodEndLabel}. Thanks for the honest feedback, it goes
          straight to the team.
        </Typography>
        <Button size="m" className="mt-2" onClick={() => onClose()}>
          Done
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 h-[min(640px,80vh)]">
      <div>
        <Typography variant="h5">Before you go</Typography>
        <Typography variant="body2" className="text-foreground-muted">
          Tell our AI assistant what&apos;s not working. The Klap team reads every conversation.
        </Typography>
      </div>

      <div ref={listRef} className="flex-1 overflow-y-auto flex flex-col gap-3 pr-1">
        {items.map((item, index) => {
          if (item.type === "applied") return <AppliedNote key={index} {...item} />;
          if (item.type === "assistant")
            return (
              <AssistantRow key={index} first={items[index - 1]?.type !== "assistant"}>
                <div className="px-4 py-2.5 rounded-2xl rounded-tl-md bg-surface-secondary whitespace-pre-wrap">
                  <Typography variant="body2">{item.text}</Typography>
                </div>
              </AssistantRow>
            );
          return (
            <div
              key={index}
              className="px-4 py-2.5 rounded-2xl rounded-br-md max-w-[85%] whitespace-pre-wrap bg-primary text-primary-foreground self-end"
            >
              <Typography variant="body2">{item.text}</Typography>
            </div>
          );
        })}
        {thinking && (
          <AssistantRow first={items.at(-1)?.type !== "assistant"}>
            {checking ? <CheckingIndicator /> : <TypingDots />}
          </AssistantRow>
        )}
      </div>

      {error && (
        <Typography variant="caption" className="text-red-600">
          {error}
        </Typography>
      )}

      {phase.name === "saved" ? (
        <div className="flex justify-end">
          <Button size="m" onClick={() => onClose()}>
            Close
          </Button>
        </div>
      ) : phase.name === "confirm_cancel" ? (
        <div className="rounded-2xl bg-surface-secondary p-4 flex flex-col gap-3">
          <Typography variant="body2">
            Your plan stays active until {periodEndLabel}. After that you lose access to your
            videos and clips, and your scheduled posts are cancelled.
          </Typography>
          <div className="flex gap-2">
            <Button size="m" variant="danger" loading={busy} onClick={confirmCancel}>
              Confirm cancellation
            </Button>
            <Button size="m" variant="tertiary" onClick={() => setPhase({ name: "chatting" })}>
              Back
            </Button>
          </div>
        </div>
      ) : (
        <>
          <form
            onSubmit={handleSubmit}
            className="flex items-end gap-2 rounded-2xl border border-border p-2 focus-within:border-foreground-muted"
          >
            <textarea
              ref={inputRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={1}
              maxLength={2000}
              disabled={phase.name !== "chatting"}
              placeholder="Write your answer…"
              className="flex-1 resize-none bg-transparent outline-none border-0 focus:ring-0 px-2 py-1.5 text-base max-h-32"
            />
            <Button
              type="submit"
              size="m"
              className="!p-2"
              aria-label="Send"
              disabled={!draft.trim() || thinking || phase.name !== "chatting"}
            >
              <ArrowUpIcon className="w-4 h-4" />
            </Button>
          </form>
          {canReachTeam && (
            <Typography variant="caption" className="text-foreground-muted -mt-2 px-1">
              Need technical support?{" "}
              <button type="button" className="underline hover:text-foreground" onClick={messageTeam}>
                Talk to the team
              </button>
            </Typography>
          )}
          {/* Close keeps the subscription. Cancelling stays one visible
              click away (then one confirmation), as a plain link. */}
          <div className="flex justify-between items-center">
            <button
              type="button"
              disabled={phase.name !== "chatting"}
              onClick={() => setPhase({ name: "confirm_cancel" })}
              className="px-1 text-sm text-foreground-secondary underline underline-offset-2 hover:text-foreground disabled:opacity-50"
            >
              Cancel subscription
            </button>
            <Button size="m" onClick={() => onClose()}>
              Close
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default RetentionChat;
